import React, { useCallback, useState } from 'react';
import { Eye, TestTube2, Edit2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import FilterBar from '../components/FilterBar';
import Modal from '../components/Modal';
import { useFetch, useFilters } from '../hooks';
import { labBookingService } from '../services/api';
import { formatDate, formatCurrency } from '../utils/helpers';

/* ── Status config ──────────────────────────────────────────────────────────── */
export const LAB_STATUS_CONFIG = {
  requested: {
    label: "Requested",
    cls: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  },
  accepted: {
    label: "Accepted",
    cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  },
  out_for_pickup: {
    label: "Out for Pickup",
    cls: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  },
  pickup_completed: {
    label: "Pickup Completed",
    cls: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300",
  },
  under_testing: {
    label: "Under Testing",
    cls: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  },
  testing_completed: {
    label: "Testing Completed",
    cls: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
  },
  cancelled: {
    label: "Cancelled",
    cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  },
};

const ALL_STATUSES = Object.keys(LAB_STATUS_CONFIG);

export const LabStatusBadge = ({ status }) => {
  const cfg = LAB_STATUS_CONFIG[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

export const getAllowedLabStatuses = (current) => {
  if (current === 'requested')        return ['accepted', 'cancelled'];
  if (current === 'accepted')         return ['out_for_pickup'];
  if (current === 'out_for_pickup')   return ['pickup_completed'];
  if (current === 'pickup_completed') return ['under_testing'];
  if (current === 'under_testing')    return ['testing_completed'];
  return [];
};

/* ── Update Status Modal ────────────────────────────────────────────────────── */
const UpdateStatusModal = ({ booking, onClose, onSuccess }) => {
  const [status,  setStatus]  = useState('');
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(false);

  const allowed = getAllowedLabStatuses(booking?.status);

  const handleConfirm = async () => {
    if (!status) { toast.error('Select a status'); return; }
    if (status === 'testing_completed' && !report) { toast.error('Please upload report first'); return; }
    setLoading(true);
    try {
      if (status === 'testing_completed') {
        await labBookingService.uploadReport(booking.id, report);
      } else {
        await labBookingService.updateStatus(booking.id, status);
      }
      toast.success('Status updated!');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to update status');
    } finally { setLoading(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Update Booking Status" size="sm">
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl">
          <span className="text-xs text-gray-500">Current:</span>
          <LabStatusBadge status={booking?.status} />
        </div>
        {allowed.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-2">No further status updates available</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {allowed.map(s => (
              <button key={s} type="button" onClick={() => { setStatus(s); setReport(null); }}
                className={`py-2 px-3 rounded-lg border text-sm font-medium capitalize transition-colors ${
                  status === s
                    ? 'bg-medihub-primary text-white border-medihub-primary'
                    : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}>
                {LAB_STATUS_CONFIG[s]?.label || s}
              </button>
            ))}
          </div>
        )}
        {status === 'testing_completed' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Upload Report <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,image/*"
              onChange={e => setReport(e.target.files[0] || null)}
              className="block w-full text-sm text-gray-600 dark:text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-medihub-light file:text-medihub-primary dark:file:bg-medihub-dark/30 dark:file:text-medihub-highlight cursor-pointer"
            />
            {report && <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ {report.name}</p>}
          </div>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleConfirm}
            disabled={!status || loading || (status === 'testing_completed' && !report)}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? 'Updating…' : 'Update Status'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '', dateFrom: '', dateTo: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: Object.entries(LAB_STATUS_CONFIG).map(([value, { label }]) => ({ value, label })),
  },
  { key: 'dateFrom', label: 'From Date', type: 'date' },
  { key: 'dateTo',   label: 'To Date',   type: 'date' },
];

const bookingFilterFn = (b, f) => {
  if (f.search) {
    const q = f.search.toLowerCase();
    if (![b.id, b.patient_name, b.lab_test_name, b.category_name].join(' ').toLowerCase().includes(q)) return false;
  }
  if (f.status   && b.status       !== f.status)   return false;
  if (f.dateFrom && b.booking_date <  f.dateFrom)  return false;
  if (f.dateTo   && b.booking_date >  f.dateTo)    return false;
  return true;
};

/* ── Stat Card ──────────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, color, bg }) => (
  <div className={`card p-4 text-center ${bg}`}>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
  </div>
);

/* ── Page ───────────────────────────────────────────────────────────────────── */
const LabBookings = () => {
  const navigate = useNavigate();
  const { data: bookings, loading, refetch } = useFetch(() => labBookingService.getAll());
  const [statusTarget, setStatusTarget] = useState(null);

  const filterFn = useCallback(bookingFilterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(
    FILTER_INITIAL, filterFn, bookings
  );

  const counts = bookings ? {
    total:     bookings.length,
    requested: bookings.filter(b => b.status === 'requested').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  } : {};

  const columns = [
    {
      key: 'id', label: 'Booking ID',
      render: v => <span className="font-mono text-xs font-semibold text-medihub-primary dark:text-medihub-highlight">#{v}</span>,
    },
    {
      key: 'patient_name', label: 'Patient',
      render: (v, row) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white text-sm">{v}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{row.patient_mobile}</p>
        </div>
      ),
    },
    {
      key: 'lab_test_name', label: 'Test',
      render: (v, row) => (
        <div className="flex items-center gap-2">
          {row.lab_test_image
            ? <img src={row.lab_test_image} alt={v} className="w-8 h-8 rounded-lg object-cover shrink-0" />
            : <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg shrink-0"><TestTube2 className="w-3.5 h-3.5 text-blue-500" /></div>
          }
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white max-w-[150px] truncate">{v}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{row.category_name}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'total_amount', label: 'Amount',
      render: v => <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(v)}</span>,
    },
    {
      key: 'booking_date', label: 'Date & Slot',
      render: (v, row) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(v)}</p>
          {row.slot_start && <p className="text-xs text-gray-500">{row.slot_start?.slice(0,5)} - {row.slot_end?.slice(0,5)}</p>}
        </div>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: v => <LabStatusBadge status={v} />,
    },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => navigate(`/lab-bookings/${row.id}`)}
            className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-blue-500" title="View Details">
            <Eye className="w-4 h-4" />
          </button>
          {row.status !== 'completed' && row.status !== 'cancelled' && (
            <button onClick={() => setStatusTarget(row)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Update Status">
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lab Bookings</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage lab test bookings</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total"     value={counts.total     || 0} color="text-gray-900 dark:text-white"        bg="" />
        <StatCard label="Requested" value={counts.requested || 0} color="text-yellow-600 dark:text-yellow-400" bg="bg-yellow-50 dark:bg-yellow-900/10" />
        <StatCard label="Completed" value={counts.completed || 0} color="text-green-600 dark:text-green-400"   bg="bg-green-50 dark:bg-green-900/10" />
        <StatCard label="Cancelled" value={counts.cancelled || 0} color="text-red-600 dark:text-red-400"       bg="bg-red-50 dark:bg-red-900/10" />
      </div>

      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search by ID, patient, test…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      <Table
        title={`Lab Bookings (${filtered.length}${activeCount ? ` of ${bookings?.length || 0}` : ''})`}
        columns={columns}
        data={filtered}
        loading={loading}
        exportFilename="lab-bookings"
      />

      {statusTarget && (
        <UpdateStatusModal
          booking={statusTarget}
          onClose={() => setStatusTarget(null)}
          onSuccess={() => { refetch(); setStatusTarget(null); }}
        />
      )}
    </div>
  );
};

export default LabBookings;
